import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

export const DOIConfirmation = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const confirmToken = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doi/confirm/${token}`);
        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(data.message);
          setEmail(data.email);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Bestätigung fehlgeschlagen');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Netzwerkfehler. Bitte versuchen Sie es später erneut.');
      }
    };

    if (token) {
      confirmToken();
    }
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="E-Mail-Bestätigung – RetailInvest"
        description="Bestätigen Sie Ihre E-Mail-Adresse für RetailInvest"
        url="/doi/confirm"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {status === 'loading' && (
            <Card className="glass-card p-12 text-center">
              <Loader2 className="h-16 w-16 mx-auto mb-6 animate-spin" style={{ color: 'var(--navy-600)' }} />
              <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Bestätige E-Mail-Adresse...
              </h1>
              <p style={{ color: 'var(--gray-600)' }}>Bitte warten Sie einen Moment.</p>
            </Card>
          )}

          {status === 'success' && (
            <Card className="glass-card p-12 text-center">
              <CheckCircle2 className="h-20 w-20 mx-auto mb-6 text-green-600" />
              <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                ✅ E-Mail-Adresse bestätigt!
              </h1>
              <p className="text-lg mb-8" style={{ color: 'var(--gray-700)' }}>
                Vielen Dank! Ihre E-Mail-Adresse <strong>{email}</strong> wurde erfolgreich bestätigt.
              </p>
              
              <div className="bg-green-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--success-700)' }}>
                  📥 Ihre kostenlosen Downloads sind bereit!
                </h3>
                <p className="mb-4" style={{ color: 'var(--gray-700)' }}>
                  Sie haben jetzt Zugang zu unseren exklusiven Lead Magnets:
                </p>
                <ul className="text-left max-w-md mx-auto space-y-2 mb-6" style={{ color: 'var(--gray-700)' }}>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    <span>Checkliste: Unterlagen für den Verkauf</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    <span>Guide: WAULT optimieren</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                    <span>Ratgeber: 7 häufigste Fehler beim Verkauf</span>
                  </li>
                </ul>
                
                <Link to="/downloads">
                  <Button className="btn-gold-gradient text-white font-semibold">
                    <Download className="h-5 w-5 mr-2" />
                    Zu den Downloads
                  </Button>
                </Link>
              </div>

              <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                Sie erhalten ab sofort unsere Informationen zu Marktentwicklungen und Investmentchancen.
              </p>
            </Card>
          )}

          {status === 'error' && (
            <Card className="glass-card p-12 text-center">
              <XCircle className="h-20 w-20 mx-auto mb-6 text-red-600" />
              <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Bestätigung fehlgeschlagen
              </h1>
              <p className="text-lg mb-8" style={{ color: 'var(--gray-700)' }}>
                {message}
              </p>
              <Link to="/kontakt">
                <Button variant="outline">
                  Zum Kontaktformular
                </Button>
              </Link>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DOIConfirmation;